import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays } from "lucide-react";
import heroImage from "@/assets/tubing/friends-floating-cooler.jpg";
import packingImg from "@/assets/cabanas/woman-with-tubes.jpg";
import familyImg from "@/assets/tubing/family-tubes.jpg";

const PUBLISH_DATE = "May 9, 2026";
const CANONICAL = "/blog/what-to-pack-tubing-guadalupe-river";
const TITLE = "What to Pack for Tubing the Guadalupe River — Family Checklist | Son's";
const DESCRIPTION =
  "The complete packing list for tubing the Guadalupe River near New Braunfels — what to bring, what to leave home, and family-friendly tips from Son's Guadalupe.";

const FAQS = [
  {
    q: "What should I wear tubing the Guadalupe River?",
    a: "A swimsuit, a rash guard or quick-dry shirt, water shoes, and a hat. The sun on the Guadalupe is no joke — sunscreen and a hat are non-negotiable.",
  },
  {
    q: "Can I bring a cooler tubing?",
    a: "Yes. We recommend a small floating cooler with a tube of its own. Glass containers are not allowed on the river — cans, pouches, and plastic only.",
  },
  {
    q: "Do I need water shoes for the Guadalupe?",
    a: "Yes. The river bottom is rocky in spots and water shoes save toes. Closed-toe water sandals work great.",
  },
  {
    q: "What should I NOT bring tubing?",
    a: "No glass, no styrofoam, no anything you can't afford to lose. Phones go in a waterproof pouch or stay locked in the car. Cabana renters can leave valuables in the car at their cabana parking spot.",
  },
];

const schemaJson = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
        { "@type": "ListItem", position: 3, name: "What to Pack for Tubing the Guadalupe River — The Family Checklist", item: `https://sonsguadalupe.com/blog/what-to-pack-tubing-guadalupe-river` },
      ],
    },
    {
      "@type": "BlogPosting",
      headline: "What to Pack for Tubing the Guadalupe River — Family Checklist",
      description: DESCRIPTION,
      image: "https://sonsguadalupe.com/og-image.png",
      author: { "@type": "Organization", name: "Son's Guadalupe" },
      datePublished: "2026-05-09",
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
      keywords:
        "what to pack tubing, guadalupe river packing list, what to bring tubing new braunfels, tubing checklist, family tubing packing",
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
  ],
};

const BlogTubingPackingList = () => {
  const related = getRelatedPosts("what-to-pack-tubing-guadalupe-river", 3);
  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} schemaJson={schemaJson} />
      <Header />
      <main className="pt-24 pb-16">
        <div className="relative h-[55vh] min-h-[400px]">
          <img src={heroImage} alt="Friends tubing the Guadalupe River with a floating cooler" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" /> {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                What to Pack for Tubing the Guadalupe River — The Family Checklist
              </h1>
            </div>
          </div>
        </div>

        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                A great day <strong>tubing the Guadalupe River</strong> comes down to two things — picking the right spot and packing right. We can't help you forget your sunscreen, but we can help you not forget it. Here's the complete family packing list for a day on the river near New Braunfels.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">The Essentials</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Swimsuit</strong> + rash guard or quick-dry shirt</li>
                <li><strong>Water shoes</strong> — the river bottom is rocky in spots</li>
                <li><strong>Reef-safe sunscreen</strong> + lip balm with SPF</li>
                <li><strong>Hat</strong> with a strap or chin cord</li>
                <li><strong>Sunglasses</strong> with a floating strap</li>
                <li><strong>Life jackets</strong> for kids (and any adult who wants one)</li>
                <li><strong>Towels</strong> + a change of dry clothes for the ride home</li>
              </ul>

              <img src={packingImg} alt="Packing tubes and gear for a day on the Guadalupe River" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">The Float-Bag</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Small <strong>floating cooler</strong> (with its own tube)</li>
                <li>Water bottles — hydration is everything</li>
                <li>Snacks: jerky, pretzels, fruit, granola bars</li>
                <li>Drinks in cans or pouches — <em>no glass</em></li>
                <li>Waterproof phone pouch</li>
                <li>Small dry bag for keys and a $20</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">For Families With Kids</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Properly fitted life jacket for every kid</li>
                <li>Extra dry shirt and shorts in a sealed bag</li>
                <li>Snacks they'll actually eat</li>
                <li>A small pool toy for back at the cabana</li>
                <li>Wipes for sandy hands and faces</li>
              </ul>

              <img src={familyImg} alt="Family with tubes ready for a Guadalupe River float" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">What NOT to Bring</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>No glass.</strong> Period.</li>
                <li>No styrofoam — bad for the river</li>
                <li>Nothing you can't afford to lose</li>
                <li>Loose phones, expensive sunglasses, paper money</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">The Pro Move: Book a Cabana</h2>
              <p>
                A <strong>cabana rental on the Guadalupe River</strong> changes how you pack — because you have a private home base. Leave dry clothes, snacks, and valuables at the cabana, float, come back, refuel, float again. Way easier than dragging everything down the river.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Tubing FAQs</h2>
              <div className="space-y-6">
                {FAQS.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-heading text-lg font-semibold text-foreground">{f.q}</h3>
                    <p className="mt-2">{f.a}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/book-now"><Button variant="default" size="lg" className="text-lg px-10 py-6">Book Your Day</Button></Link>
                <Link to="/tubing"><Button variant="outline" size="lg" className="text-lg px-10 py-6">Tubing Info</Button></Link>
              </div>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />

      </main>
      <SEOContentBlock
        eyebrow="Plan The Perfect Float Day"
        heading="Pack Smart, Float Easy"
        paragraphs={[
          "Pair this packing list with a cabana rental on the Guadalupe River and tubing near New Braunfels becomes the easiest family day of the summer. Son's Guadalupe handles parking, river access, and your private basecamp.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "Glamping", to: "/glamping" },
          { label: "FAQs", to: "/faqs" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogTubingPackingList;
